import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Mail, RefreshCw, ShieldCheck } from 'lucide-react';

import { checkEmailVerified, resendVerification } from '../api/client';
import { Button } from '../components/ui/button';
import { maskEmail } from '../lib/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { useAuth } from '../hooks/useAuth';

const RESEND_COOLDOWN_SECONDS = 30;
const POLL_INTERVAL_MS = 8000;

export default function VerifyEmailPage() {
  const { isAuthenticated, emailVerified, user, login, isLoading, refreshUser, whitelistBlocked } = useAuth();

  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [pollError, setPollError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  // Polling ativo enquanto autenticado e e-mail não verificado
  const polling = isAuthenticated && !emailVerified;

  useEffect(() => {
    if (!polling) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;

    const check = async () => {
      if (cancelled) return;
      try {
        const result = await checkEmailVerified();
        consecutiveFailures = 0;
        setPollError(false);

        if (result.verified && !cancelled) {
          try {
            await refreshUser();
          } catch {
            // Backend confirmou verificação mas refreshUser falhou.
            // Recarrega a página para obter novo JWT e estado limpo.
            window.location.assign('/app');
          }
          return;
        }
      } catch {
        consecutiveFailures++;
        if (consecutiveFailures >= 3) {
          setPollError(true);
          return; // Para o polling — usuário decide quando tentar novamente
        }
      }
      if (!cancelled) {
        timeoutId = setTimeout(check, POLL_INTERVAL_MS);
      }
    };

    check();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [polling, refreshUser, retryKey]);

  const handleRetryPolling = useCallback(() => {
    setPollError(false);
    setRetryKey((k) => k + 1);
  }, []);

  // Cooldown do reenvio
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown((c) => c - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = useCallback(async () => {
    setResendStatus('sending');
    try {
      await resendVerification();
      setResendStatus('sent');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setResendStatus('error');
    }
  }, []);

  const handleReauthenticate = useCallback(() => {
    login();
  }, [login]);

  if (isLoading) return null;

  // Defesa extra: se o usuário está bloqueado pela whitelist do piloto,
  // não renderizar a tela de verificação (que apareceria com email vazio).
  // O gate em App.tsx já cuida da renderização da BlockedPage; este return
  // existe como cinto-e-suspensórios para janelas onde a ordem de avaliação
  // dos hooks/estado possa permitir esta página rodar.
  if (whitelistBlocked) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (emailVerified) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ backgroundColor: 'var(--color-bg-dark)' }}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
               style={{ backgroundColor: 'rgba(122, 36, 48, 0.1)' }}>
            <Mail className="h-8 w-8" style={{ color: 'var(--color-primary)' }} />
          </div>
          <CardTitle className="text-xl">
            Verifique seu e-mail para continuar
          </CardTitle>
          <CardDescription className="mt-2">
            Enviamos um link de verificação para{' '}
            <strong style={{ color: 'var(--color-text-primary)' }}>
              {maskEmail(user?.email)}
            </strong>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Indicador de polling */}
          {pollError ? (
            <div className="flex flex-col items-center gap-2 rounded-lg p-3 text-sm"
                 style={{
                   backgroundColor: 'var(--color-error-bg, rgba(220, 38, 38, 0.1))',
                   color: 'var(--color-error)',
                 }}>
              <span>Não foi possível verificar o status do e-mail.</span>
              <Button variant="outline" size="sm" onClick={handleRetryPolling}>
                Tentar novamente
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 rounded-lg p-3 text-sm"
                 style={{
                   backgroundColor: 'var(--color-info-bg)',
                   color: 'var(--color-info)',
                 }}>
              {polling && <RefreshCw className="h-4 w-4 animate-spin" />}
              <span>Aguardando confirmação...</span>
            </div>
          )}

          {/* Botão re-autenticar */}
          <Button
            onClick={handleReauthenticate}
            className="w-full"
            variant="default"
          >
            Já confirmei meu e-mail
          </Button>

          {/* Botão reenviar */}
          <Button
            onClick={handleResend}
            className="w-full"
            variant="outline"
            disabled={resendCooldown > 0 || resendStatus === 'sending'}
          >
            {resendStatus === 'sending'
              ? 'Enviando...'
              : resendCooldown > 0
                ? `Reenviar e-mail (${resendCooldown}s)`
                : 'Reenviar e-mail de verificação'}
          </Button>

          {resendStatus === 'sent' && (
            <p className="text-center text-sm"
               style={{ color: 'var(--color-success)' }}>
              E-mail reenviado com sucesso!
            </p>
          )}

          {resendStatus === 'error' && (
            <p className="text-center text-sm"
               style={{ color: 'var(--color-error)' }}>
              Falha ao reenviar. Tente novamente em alguns instantes.
            </p>
          )}

          {/* Dica sobre spam */}
          <p className="text-center text-xs"
             style={{ color: 'var(--color-text-muted)' }}>
            Verifique sua caixa de spam ou lixo eletrônico caso não
            encontre o e-mail na caixa de entrada.
          </p>
        </CardContent>

        <CardFooter>
          <div className="w-full rounded-lg p-3 text-xs"
               style={{
                 backgroundColor: 'var(--color-bg-elevated)',
                 color: 'var(--color-text-secondary)',
               }}>
            <div className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0"
                           style={{ color: 'var(--color-success)' }} />
              <span>
                A verificação de e-mail protege sua conta contra acesso
                não autorizado e garante que você receba notificações
                importantes sobre seus processos.
              </span>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
